/**
 * CanSeeTargetCondition - behavior tree condition checking line of sight to target.
 */

import * as THREE from 'three';

import { EngineClass } from '../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../BehaviorNode.js';
import { ConditionEvaluator, type ConditionEvaluatorOptions } from '../ConditionEvaluator.js';

import type { Actor } from '../../../../actors/Actor.js';
import type { Blackboard } from '../../blackboard/Blackboard.js';

export interface CanSeeTargetConditionOptions extends ConditionEvaluatorOptions {
  targetActorKey?: string;
  targetPositionKey?: string;
  maxDistance?: number;
  eyeHeightOffset?: number;
  targetHeightOffset?: number;
}

@EngineClass('CanSeeTargetCondition')
export class CanSeeTargetCondition extends ConditionEvaluator {
  private targetActorKey?: string;
  private targetPositionKey?: string;
  private maxDistance: number;
  private eyeHeightOffset: number;
  private targetHeightOffset: number;

  constructor(options: CanSeeTargetConditionOptions = {}) {
    super({
      name: 'CanSeeTarget',
      ...options
    });

    this.targetActorKey = options.targetActorKey ?? 'PlayerActor';
    this.targetPositionKey = options.targetPositionKey;
    this.maxDistance = options.maxDistance ?? 50.0;
    this.eyeHeightOffset = options.eyeHeightOffset ?? 1.7;
    this.targetHeightOffset = options.targetHeightOffset ?? 1.7;
  }

  protected override async onEvaluate(blackboard: Blackboard): Promise<BehaviorStatus> {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return BehaviorStatus.Failure;
    }

    // Get target position
    const targetPos = this.getTargetPosition(blackboard);
    if (!targetPos) {
      return BehaviorStatus.Failure;
    }

    // Get owner position
    const ownerPosition = new THREE.Vector3();
    owner.rootComponent.getWorldPosition(ownerPosition);

    // Calculate eye position and target position with height offsets
    const eyePosition = ownerPosition.clone();
    eyePosition.y += this.eyeHeightOffset;

    const targetPosition = targetPos.clone();
    targetPosition.y += this.targetHeightOffset;

    // Check distance first
    const distance = eyePosition.distanceTo(targetPosition);
    if (distance > this.maxDistance) {
      return BehaviorStatus.Failure;
    }

    // Perform raycast to check line of sight
    const direction = targetPosition.clone().sub(eyePosition).normalize();
    const raycaster = new THREE.Raycaster(eyePosition, direction, 0, distance);

    // Get the world's scene for raycasting
    const world = owner.getWorld();
    if (!world) {
      return BehaviorStatus.Failure;
    }

    // Get all mesh objects for raycasting, excluding sprites and other non-physical objects
    const raycastTargets: THREE.Object3D[] = [];

    const collectMeshObjects = (obj: THREE.Object3D) => {
      // Only include mesh objects that should block line of sight
      if (obj instanceof THREE.Mesh && obj.matrixWorld) {
        raycastTargets.push(obj);
      }

      // Recursively check children, but skip sprites entirely
      obj.children.forEach(child => {
        if (!(child instanceof THREE.Sprite)) {
          collectMeshObjects(child);
        }
      });
    };

    // Collect all valid mesh objects from the scene
    world.scene.children.forEach(child => {
      if (!(child instanceof THREE.Sprite)) {
        collectMeshObjects(child);
      }
    });

    // Perform the raycast (recursive: false since we already collected all meshes)
    const intersects = raycaster.intersectObjects(raycastTargets, false);

    // Filter out intersections with the owner and target
    const validIntersects = intersects.filter(intersect => {
      const intersectedObject = intersect.object;
      if (!intersectedObject) {
        return false;
      }

      // Skip if it's part of the owner or target
      let current: THREE.Object3D | null = intersectedObject;
      while (current) {
        // Add null check for matrixWorld and other properties
        if (!current.matrixWorld) {
          current = current.parent;
          continue;
        }

        if (current.userData?.actor === owner) {
          return false;
        }

        // Check if it's the target actor
        if (this.targetActorKey) {
          const targetActor = blackboard.getValue<Actor>(this.targetActorKey);
          if (targetActor && current.userData?.actor === targetActor) {
            return false;
          }
        }

        current = current.parent;
      }

      return true;
    });

    // If there are no valid intersections, we have line of sight
    return validIntersects.length === 0 ? BehaviorStatus.Success : BehaviorStatus.Failure;
  }

  private getTargetPosition(blackboard: Blackboard): THREE.Vector3 | null {
    if (this.targetActorKey) {
      const actor = blackboard.getValue<Actor>(this.targetActorKey);
      if (actor) {
        const position = new THREE.Vector3();
        actor.rootComponent.getWorldPosition(position);
        return position;
      }
    }

    if (this.targetPositionKey) {
      const position = blackboard.getValue<THREE.Vector3>(this.targetPositionKey);
      if (position) {
        return position.clone();
      }
    }

    return null;
  }
}